import type { QueryObserverResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	AddManualPriceBody,
	UpdateCarrierPriceBody,
} from "~/shared/schemas/carrier-price-schema";
import {
	addManualPriceBodySchema,
	updateCarrierPriceBodySchema,
} from "~/shared/schemas/carrier-price-schema.ts";
import { carrierPriceTypeSchema } from "~/shared/schemas/carrier-price-type-schema.ts";
import type {
	CarrierPrice,
	SelectedCarrier,
} from "~/shared/schemas/carrier-schema.ts";
import {
	calculateInsuranceMarkupsSchema,
	carrierPriceSchema,
	foundCarrierInsuranceMarkup,
	selectCarrierSchema,
} from "~/shared/schemas/carrier-schema.ts";
// import { customerDetailsSchema } from "~/shared/schemas/customer-schema.ts";
import {
	MarkupsPartialsSchema,
	MarkupsSchema,
} from "~/shared/schemas/markup-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { quoteResponseSchema } from "~/shared/schemas/quote-schema.ts";
import { serviceLevelSchema } from "~/shared/schemas/service-level-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	postAndValidate,
	putAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import type { ApiResult } from "~/shared/types/api-types.ts";
import type {
	IdAndCarrierPrices,
	IdCarrierPricesAndCommodities,
	QuoteStateType,
	SelectedCarrierAndEtag,
} from "~/shared/types/quote-details-types.ts";
import { ddSaveQuote, toDdWorkflow } from "~/shared/utils/datadog-actions.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";

import {
	createLtlMutationErrorHandler,
	getLtlFallbackMessage,
} from "../utils/ltl-error-handler.ts";
import { useLtlActionErrorHandler } from "./use-ltl-action-error-handler.tsx";
import { useQuoteLoader } from "./use-quote-details-hooks.ts";

export type CarrierSelectionPageLoadData = {
	// customerData: {
	// 	id: number;
	// 	name: string;
	// 	hasSignedLTLAddendum: boolean;
	// 	status: {
	// 		id: number;
	// 		name: string;
	// 	};
	// 	primaryBroker: {
	// 		id: number;
	// 		name: string;
	// 		email: string;
	// 	};
	// 	ltlBroker: {
	// 		id: number;
	// 		name: string;
	// 		email: string;
	// 	} | null;
	// };
	serviceLevelData: {
		id: number;
		name: string;
	}[];
	carrierPriceTypeData: {
		id: number;
		name: string;
	}[];
};

export const useCarrierSelectionPageLoadQueries = (
	customerId: number,
	options?: { enabled?: boolean; retry?: boolean | number },
): QueryObserverResult<CarrierSelectionPageLoadData> => {
	const isEnabled = options?.enabled ?? customerId > 0;

	const query = useQuery<CarrierSelectionPageLoadData>({
		queryKey: ["carrierSelectionPageLoad", customerId, isEnabled],
		queryFn: async (): Promise<CarrierSelectionPageLoadData> => {
			const [
				// customerDataResult,
				serviceLevelResult,
				carrierPriceTypeResult,
			] = await Promise.all([
				// getAndValidate(
				// 	`${apiRoutes.SELECTCUSTOMER}${customerId}`,
				// 	customerDetailsSchema,
				// ),
				getAndValidate(apiRoutes.SERVICELEVELS, serviceLevelSchema.array()),
				getAndValidate(
					apiRoutes.CARRIERPRICETYPES,
					carrierPriceTypeSchema.array(),
				),
			]);

			// Handle validation errors by throwing - React Query will handle retries
			// if (!customerDataResult.success) {
			// 	throw new Error(
			// 		`Customer data validation failed: ${customerDataResult.error}`,
			// 	);
			// }

			if (!serviceLevelResult.success) {
				throw new Error(
					`Service level data validation failed: ${serviceLevelResult.error}`,
				);
			}

			if (!carrierPriceTypeResult.success) {
				throw new Error(
					`Carrier Price Type data validation failed: ${carrierPriceTypeResult.error}`,
				);
			}

			return {
				// customerData: customerDataResult.data,
				serviceLevelData: serviceLevelResult.data,
				carrierPriceTypeData: carrierPriceTypeResult.data,
			};
		},
		staleTime: 1000 * 60 * 60 * 24 * 2, // 2 days
		gcTime: 1000 * 60 * 60 * 24 * 3, // 3 days
		retry: options?.retry ?? 5,
		enabled: isEnabled,
	});

	// Customer details are already managed by other hooks in the application
	// Removed setCustomerDetails calls to prevent infinite loops during quote revision

	return query;
};

export const useSaveSelectedCarrier = () => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (body: SelectedCarrier) => {
			const url = isInternal
				? apiRoutes.SELECTCARRIER
				: apiRoutes.CUSTOMERSELECTCARRIER;
			return await putAndValidate(
				`${url.replace(":quoteId", String(body.id))}`,
				body,
				quoteResponseSchema,
				{
					overrideHook: (request) => {
						request.headers.set("If-Match", body.eTag ?? "");
					},
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
		onSuccess: (data, variables) => {
			// Invalidate the quote query cache to ensure fresh data on navigation
			queryClient.invalidateQueries({
				queryKey: ["getQuote", variables.id],
			});
			// Also update the cache directly with the response data
			if (data.success) {
				queryClient.setQueryData(["getQuote", variables.id], data.data);
			}
		},
	});
};

export const useBookCarrier = () => {
	const { mergeResponseIntoQuoteState, onChange, quoteState } =
		useContext(QuoteDetailsContext);
	const { mutateAsync } = useSaveSelectedCarrier();
	const navigate = useNavigate();
	const location = useLocation();

	const bookCarrier = async (
		carrierPriceId: number,
		eTagOverride?: string,
	): Promise<ApiResult<void>> => {
		const body = {
			id: quoteState.id,
			eTag: eTagOverride ?? quoteState.eTag,
			selectedCarrierPriceId: carrierPriceId,
			insuranceApplyTypeId: null,
			insuranceProductTypeId: null,
			insuranceValue: null,
		};
		const parsedJson = selectCarrierSchema.safeParse(body);

		if (!parsedJson.success) {
			console.error("Error validating carrier selection:", parsedJson.error);
			return {
				success: false,
				error: "Invalid carrier selection request.",
				details: parsedJson.error,
			};
		}

		try {
			const result = await mutateAsync(parsedJson.data);

			if (!result.success) {
				console.error(
					"Error applying selected Carrier to Quote:",
					result.error,
				);
				return { success: false, error: result.error };
			}

			onChange("selectedCarrierPriceId", carrierPriceId);
			mergeResponseIntoQuoteState({
				eTag: result.data.eTag,
				carrierPrices: result.data.carrierPrices as CarrierPrice[],
			});
			// Navigate - route change will automatically update currentStep via useEffect
			const destinationRoute = location.pathname.startsWith(routes.ltlShipment)
				? routes.ltlShipmentCarrierSelection
				: routes.shipmentDetails;
			navigate(destinationRoute.replace(":quoteId", quoteState.id.toString()), {
				replace: true,
			});
			// Reset scroll to the top of the page so the shipment details page
			// starts at the top instead of inheriting the carrier selection
			// page's scroll position.
			scrollMainContentToTop();
			return { success: true, data: undefined };
		} catch (error) {
			console.error("Error during carrier booking:", error);
			return {
				success: false,
				error: error instanceof Error ? error.message : "Unknown error",
			};
		}
	};

	return { bookCarrier };
};

export const useAddManualPrice = () => {
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (body: {
			quoteId: number;
			eTag: string;
			carrier: AddManualPriceBody;
		}) => {
			return await postAndValidate(
				`${apiRoutes.ADDMANUALPRICE.replace(":quoteId", String(body.quoteId))}`,
				body.carrier,
				carrierPriceSchema,
				{
					overrideHook: (request) => {
						request.headers.set("If-Match", body.eTag ?? "");
					},
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
	});
};

export const useMutateCarriers = () => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();

	const mutation = useMutation({
		mutationFn: async (body: {
			quoteId: number;
			eTag: string;
			carriers: UpdateCarrierPriceBody[];
		}): Promise<ApiResult<QuoteResponse>> => {
			return await putAndValidate(
				`${apiRoutes.UPDATEPRICES.replace(":quoteId", String(body.quoteId))}`,
				body.carriers,
				quoteResponseSchema,
				{
					overrideHook: (request) => {
						request.headers.set("If-Match", body.eTag ?? "");
					},
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
		onSuccess: (data, variables) => {
			// Invalidate the quote query cache to ensure fresh data on navigation
			queryClient.invalidateQueries({
				queryKey: ["getQuote", variables.quoteId],
			});
			// Also update the cache directly with the response data
			if (data.success) {
				queryClient.setQueryData(["getQuote", variables.quoteId], data.data);
			}
		},
	});

	return {
		...mutation,
		isPending: mutation.isPending,
	};
};

export const useUpdateAllCarrierMargins = () => {
	const { mutateAsync, isPending } = useMutateCarriers();
	const { mergeResponseIntoQuoteState, workflow } =
		useContext(QuoteDetailsContext);
	const { handleLtlActionError, refreshModal, isRefreshing } =
		useLtlActionErrorHandler();

	const updateAllCarrierMargins = async (
		quoteId: number,
		eTag: string,
		carriers: CarrierPrice[],
	) => {
		const mappedCarriers: UpdateCarrierPriceBody[] = carriers
			.filter((carrier) => carrier.id >= 0)
			.map((carrier) => ({
				Id: carrier.id,
				CarrierId: carrier.carrierId,
				CarrierPriceTypeId: carrier.carrierPriceTypeId,
				CarrierRate: carrier.carrierRate,
				BrokerMargin: carrier.brokerMargin,
				CarrierQuoteId: carrier.carrierQuoteId ?? "",
				CarrierQuoteExpirationDate: carrier.carrierQuoteExpirationDate,
				ServiceLevelId: carrier.serviceLevel.id,
				TransitDays: carrier.transitDays,
			}));

		const parsedJson = updateCarrierPriceBodySchema
			.array()
			.safeParse(mappedCarriers);

		if (parsedJson.success) {
			const body = {
				quoteId,
				eTag,
				carriers: Array.isArray(parsedJson.data) ? parsedJson.data : [],
			};
			const result = await mutateAsync(body);
			if (result.success) {
				// Update local state with the response data to keep UI in sync.
				// Clear the selected carrier since margins changed its pricing.
				mergeResponseIntoQuoteState({
					id: result.data.id,
					carrierPrices: result.data.carrierPrices,
					eTag: result.data.eTag,
					selectedCarrierPriceId: null,
				});
				ddSaveQuote(String(quoteId), toDdWorkflow(workflow));
				return result.data.eTag;
			} else {
				console.error("Error updating all carrier margins:", result.error);
				handleLtlActionError(result, "save");
			}
		} else {
			console.error(
				"Error validating update all carrier margins:",
				parsedJson.error,
			);
			handleLtlActionError({ success: false });
		}
		return undefined;
	};

	return { updateAllCarrierMargins, isPending, refreshModal, isRefreshing };
};

export const insuranceMarkupMutationKey = [
	"carrierSelection",
	"insuranceMarkup",
] as const;

export type InsuranceMarkupVariables = {
	legacyId: number;
	carrierId: number;
	carrierPriceId?: number;
};

export const useGetInsuranceMarkup = () => {
	return useMutation({
		mutationKey: insuranceMarkupMutationKey,
		mutationFn: async ({ legacyId, carrierId }: InsuranceMarkupVariables) => {
			return await getAndValidate(
				`${apiRoutes.GETINSURANCEMARKUP}?legacyId=${legacyId}&carrierId=${carrierId}`,
				foundCarrierInsuranceMarkup,
			);
		},
	});
};

export const calculateInsuranceMarkupsMutationKey = [
	"carrierSelection",
	"calculateInsuranceMarkups",
] as const;

export type CalculateInsuranceMarkupsVariables = {
	quoteId: number;
	insuranceValue: number;
};

export const useCalculateInsuranceMarkups = () => {
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationKey: calculateInsuranceMarkupsMutationKey,
		mutationFn: async ({
			quoteId,
			insuranceValue,
		}: CalculateInsuranceMarkupsVariables) => {
			const route = isInternal
				? apiRoutes.CALCULATEINSURANCEMARKUPS
				: apiRoutes.CALCULATECUSTOMERINSURANCEMARKUPS;
			return await postAndValidate(
				route.replace(":quoteId", String(quoteId)),
				{ insuranceValue },
				calculateInsuranceMarkupsSchema,
			);
		},
	});
};

export const ltlMarkupsMutationKey = [
	"carrierSelection",
	"ltlMarkups",
] as const;

export type LtlMarkupsVariables = {
	customerId?: number;
};

export const useGetLtlMarkups = () => {
	return useMutation({
		mutationKey: ltlMarkupsMutationKey,
		mutationFn: async ({ customerId }: LtlMarkupsVariables) => {
			const url =
				customerId === undefined
					? apiRoutes.MARKUPDEFAULTSLTL
					: `${apiRoutes.MARKUPDEFAULTSLTL}?customerId=${customerId}`;
			return await getAndValidate(url, MarkupsSchema);
		},
	});
};

export const partialsMarkupMutationKey = [
	"carrierSelection",
	"partialsMarkup",
] as const;

export type PartialsMarkupVariables = {
	customerId?: number;
};

export const useGetPartialsMarkup = () => {
	return useMutation({
		mutationKey: partialsMarkupMutationKey,
		mutationFn: async ({ customerId }: PartialsMarkupVariables) => {
			const url =
				customerId === undefined
					? apiRoutes.MARKUPDEFAULTSPARTIALS
					: `${apiRoutes.MARKUPDEFAULTSPARTIALS}?customerId=${customerId}`;
			return await getAndValidate(url, MarkupsPartialsSchema);
		},
	});
};

export type SavedAdHocCarrier = {
	carrierPrice: CarrierPrice;
	eTag: string;
};

const saveInitialAdHocCarrier = async (
	carrier: CarrierPrice,
	quoteId: number,
	eTag: string,
	addManualPrice: ReturnType<typeof useAddManualPrice>["mutateAsync"],
	quoteState: QuoteStateType,
	mergeResponseIntoQuoteState: (
		dataToMerge:
			| IdCarrierPricesAndCommodities
			| SelectedCarrierAndEtag
			| IdAndCarrierPrices,
	) => void,
): Promise<ApiResult<SavedAdHocCarrier>> => {
	const formattedCarrier: AddManualPriceBody = {
		carrierRate: carrier.carrierRate,
		customerRate: carrier.customerRate,
		brokerMargin: carrier.brokerMargin,
		carrierPriceType: {
			id: carrier.carrierPriceTypeId,
			name: "Manual",
		},
		serviceLevel: carrier.serviceLevel,
		quoteId: quoteId,
		carrier: carrier.carrier,
		carrierId: carrier.carrierId,
		carrierPriceTypeId: carrier.carrierPriceTypeId,
		carrierQuoteId: carrier.carrierQuoteId ?? "",
		carrierQuoteExpirationDate: carrier.carrierQuoteExpirationDate,
		serviceLevelId: carrier.serviceLevel.id,
		transitDays: carrier.transitDays,
		maxLiabilityNew: carrier.maxLiabilityNew,
		maxLiabilityUsed: carrier.maxLiabilityUsed,
		insuranceMarkup: carrier.insuranceMarkup ?? 0,
		maxLiabilityOverride: carrier.maxLiabilityOverride,
		carrierTotalAccessorialCharges: carrier.carrierTotalAccessorialCharges ?? 0,
		carrierTotalAccessorialFuelSurcharges:
			carrier.carrierTotalAccessorialFuelSurcharges ?? 0,
		customerTotalAccessorialCharges:
			carrier.customerTotalAccessorialCharges ?? 0,
		customerTotalAccessorialFuelSurcharges:
			carrier.customerTotalAccessorialFuelSurcharges ?? 0,
	};

	const parsedJson = addManualPriceBodySchema.safeParse(formattedCarrier);

	if (parsedJson.success) {
		const body = {
			quoteId,
			eTag,
			carrier: parsedJson.data,
		};
		const result = await addManualPrice(body);
		if (result.success) {
			const newCarrierPrice: CarrierPrice = {
				...result.data,
				isAdHoc: true,
			};
			const savedETag = result.data.eTag ?? eTag;
			const updatedCarriers = quoteState.carrierPrices.map((c) =>
				c.id === carrier.id ? newCarrierPrice : c,
			);
			mergeResponseIntoQuoteState({
				eTag: savedETag,
				carrierPrices: updatedCarriers,
			});
			return {
				success: true,
				data: { carrierPrice: newCarrierPrice, eTag: savedETag },
			};
		} else {
			console.error("Error saving new carrier:", result.error);
			return { success: false, error: result.error };
		}
	} else {
		console.error("Error validating save new carrier:", parsedJson.error);
		return {
			success: false,
			error: "Invalid ad hoc carrier save request.",
			details: parsedJson.error,
		};
	}
};

const updateExistingAdHocCarrier = async (
	carrier: CarrierPrice,
	quoteId: number,
	eTag: string,
	updateCarriers: ReturnType<typeof useMutateCarriers>["mutateAsync"],
	quoteState: QuoteStateType,
	mergeResponseIntoQuoteState: (
		dataToMerge:
			| IdCarrierPricesAndCommodities
			| SelectedCarrierAndEtag
			| IdAndCarrierPrices,
	) => void,
): Promise<ApiResult<SavedAdHocCarrier>> => {
	const formattedCarrier: UpdateCarrierPriceBody = {
		Id: carrier.id,
		CarrierId: carrier.carrierId,
		CarrierPriceTypeId: carrier.carrierPriceTypeId,
		CarrierRate: carrier.carrierRate,
		BrokerMargin: carrier.brokerMargin,
		CarrierQuoteId: carrier.carrierQuoteId ?? "",
		CarrierQuoteExpirationDate: carrier.carrierQuoteExpirationDate,
		ServiceLevelId: carrier.serviceLevel.id,
		TransitDays: carrier.transitDays,
	};

	const parsedJson = updateCarrierPriceBodySchema.safeParse(formattedCarrier);

	if (parsedJson.success) {
		const body = {
			quoteId,
			eTag,
			carriers: [parsedJson.data],
		};
		const result = await updateCarriers(body);
		if (result.success) {
			// Preserve isAdHoc flag from existing carriers when merging API response
			const carrierPricesWithAdHocFlag = result.data.carrierPrices.map(
				(updatedCarrier: CarrierPrice) => {
					const existingCarrier = quoteState.carrierPrices.find(
						(c) => c.id === updatedCarrier.id,
					);
					return existingCarrier?.isAdHoc
						? ({ ...updatedCarrier, isAdHoc: true } as CarrierPrice)
						: updatedCarrier;
				},
			);

			mergeResponseIntoQuoteState({
				id: result.data.id,
				carrierPrices: carrierPricesWithAdHocFlag,
				eTag: result.data.eTag,
			});
			return {
				success: true,
				data: {
					carrierPrice:
						carrierPricesWithAdHocFlag.find(
							(updatedCarrier) => updatedCarrier.id === carrier.id,
						) ?? carrier,
					eTag: result.data.eTag,
				},
			};
		} else {
			console.error("Error updating ad hoc carrier:", result.error);
			return { success: false, error: result.error };
		}
	} else {
		console.error("Error validating update ad hoc carrier:", parsedJson.error);
		return {
			success: false,
			error: "Invalid ad hoc carrier update request.",
			details: parsedJson.error,
		};
	}
};

export const useSaveAdHocCarrier = () => {
	const { mutateAsync: addManualPrice } = useAddManualPrice();
	const { mutateAsync: updateCarriers } = useMutateCarriers();
	const { mergeResponseIntoQuoteState, quoteState } =
		useContext(QuoteDetailsContext);

	const saveAdHocCarrier = useCallback(
		async (carrier: CarrierPrice, quoteId: number, eTag: string) => {
			const isInitialSave = carrier.id < 0;

			return isInitialSave
				? await saveInitialAdHocCarrier(
						carrier,
						quoteId,
						eTag,
						addManualPrice,
						quoteState,
						mergeResponseIntoQuoteState,
					)
				: await updateExistingAdHocCarrier(
						carrier,
						quoteId,
						eTag,
						updateCarriers,
						quoteState,
						mergeResponseIntoQuoteState,
					);
		},
		[addManualPrice, updateCarriers, mergeResponseIntoQuoteState, quoteState],
	);

	return { saveAdHocCarrier };
};

export const useMutateContractedPrice = () => {
	const { isInternal } = useDeploymentType();
	return useMutation({
		mutationFn: async (body: CarrierPrice) => {
			return await putAndValidate(
				`${apiRoutes.UPDATECONTRACTEDPRICE.replace(":quoteId", String(body.quoteId)).replace(":carrierPriceId", String(body.id))}`,
				body,
				quoteResponseSchema,
				{
					overrideHook: (request) => {
						request.headers.set("If-Match", body.eTag ?? "");
					},
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
	});
};

export const useUpdateContractedPrice = () => {
	const { mutateAsync } = useMutateContractedPrice();
	const { handleLtlActionError, refreshModal, isRefreshing } =
		useLtlActionErrorHandler();

	const updateContractedPrice = async (carrierPrice: CarrierPrice) => {
		const parsedJson = carrierPriceSchema.safeParse(carrierPrice);

		if (parsedJson.success) {
			const result = await mutateAsync(parsedJson.data);
			if (!result.success) {
				console.error("Error updating contracted price:", result.error);
				handleLtlActionError(result, "save");
			}
		} else {
			console.error(
				"Error validating update contracted price:",
				parsedJson.error,
			);
			handleLtlActionError({ success: false });
		}
	};

	return { updateContractedPrice, refreshModal, isRefreshing };
};

// Custom hook for managing modal states
export const useModalManager = () => {
	const [showModal, setShowModal] = useState({
		addCarrier: false,
		addInsurance: false,
		confirmationRate: false,
		carrierError: false,
	});

	const toggleModal = useCallback((modalName = "") => {
		setShowModal((prevState) => ({
			...prevState,
			[modalName]: !prevState[modalName as keyof typeof prevState],
		}));
	}, []);

	return { showModal, toggleModal };
};

// Custom hook for managing margin adjustments
export const useMarginManager = () => {
	const [marginType, setMarginType] = useState<"percentage" | "flat">(
		"percentage",
	);
	const [marginValue, setMarginValue] = useState<number | null>(null);
	const { updateAllCarrierMargins, isPending, refreshModal, isRefreshing } =
		useUpdateAllCarrierMargins();

	const handleMarginTypeChange = (newMarginType: "percentage" | "flat") => {
		setMarginType(newMarginType);
		setMarginValue(null);
	};

	const handleAdjustClick = (quoteState: QuoteStateType) => {
		const updatedCarriers = quoteState.carrierPrices.map((carrier) => {
			const { carrierRate } = carrier;
			const newMargin =
				marginType === "percentage"
					? carrierRate * (marginValue ?? 1)
					: (marginValue ?? 0);
			return { ...carrier, brokerMargin: newMargin };
		});

		updateAllCarrierMargins(quoteState.id, quoteState.eTag, updatedCarriers);
	};

	return {
		marginType,
		marginValue,
		setMarginValue,
		handleMarginTypeChange,
		handleAdjustClick,
		isPending,
		refreshModal,
		isRefreshing,
	};
};

// Custom hook for quote loading and page data management
export const useCarrierPageLoader = ({
	skipQuoteFetch = false,
}: {
	skipQuoteFetch?: boolean;
} = {}) => {
	// Use the unified quote loader (no customer data fetching for carrier page)
	const quoteLoader = useQuoteLoader({
		includeCustomerData: false,
		includeFormReset: false,
		skipQuoteFetch,
	});
	const { quoteState } = useContext(QuoteDetailsContext);
	const [triggerPageQueries, setTriggerPageQueries] = useState(false);

	const customerId = quoteState.customer?.id ?? 0;
	const shouldRunPageQueries =
		!quoteLoader.shouldFetchQuote || quoteLoader.quoteIsFetched;

	useEffect(() => {
		if (shouldRunPageQueries && customerId > 0) {
			setTriggerPageQueries(true);
		}
	}, [shouldRunPageQueries, customerId]);

	const {
		data: pageData,
		isLoading: pageDataIsLoading,
		isFetched: pageDataIsFetched,
		refetch: refetchPageData,
	} = useCarrierSelectionPageLoadQueries(customerId, {
		enabled: triggerPageQueries,
	});

	useEffect(() => {
		if (
			shouldRunPageQueries &&
			customerId > 0 &&
			!pageDataIsLoading &&
			!pageData &&
			refetchPageData
		) {
			refetchPageData();
		}
	}, [
		shouldRunPageQueries,
		customerId,
		pageDataIsLoading,
		pageData,
		refetchPageData,
	]);

	return {
		pageData,
		pageDataIsLoading,
		pageDataIsFetched,
		shouldFetchQuote: quoteLoader.shouldFetchQuote,
		isLoadingQuote: quoteLoader.isLoadingQuote,
		quoteIsFetched: quoteLoader.quoteIsFetched,
	};
};

// Custom hook for booking state management
export const useBookingManager = () => {
	const [isBooking, setIsBooking] = useState(false);

	const handleBookingStart = () => {
		setIsBooking(true);
	};

	const handleStepChange = () => {
		setIsBooking(false);
	};

	return {
		isBooking,
		handleBookingStart,
		handleStepChange,
	};
};
